import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { DataTable, EmptyState, ErrorState, FieldGroup, FilterGrid, LoadingState, MvpCard, MvpTalentLayout, PaginationControls } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const AuditPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => ({ page: Number(searchParams.get('page') || 1), limit: 20, actorUserId: searchParams.get('actorUserId') || '', action: searchParams.get('action') || '', targetType: searchParams.get('targetType') || '', instanceId: searchParams.get('instanceId') || '' }), [searchParams]);
  const { data, loading, error, reload } = useAsyncData((signal) => platformCoreNetworkApi.listAuditEvents(filters, { signal }), [filters.page, filters.actorUserId, filters.action, filters.targetType, filters.instanceId]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  return (
    <MvpTalentLayout title="Platform Audit" subtitle="Immutable platform audit events from the protected Core Network control APIs.">
      <MvpCard title="Filters">
        <FilterGrid>
          <FieldGroup label="Actor user ID"><input value={filters.actorUserId} onChange={(event) => updateFilter('actorUserId', event.target.value)} /></FieldGroup>
          <FieldGroup label="Action"><input value={filters.action} onChange={(event) => updateFilter('action', event.target.value)} /></FieldGroup>
          <FieldGroup label="Target type"><input value={filters.targetType} onChange={(event) => updateFilter('targetType', event.target.value)} /></FieldGroup>
          <FieldGroup label="Instance ID"><input value={filters.instanceId} onChange={(event) => updateFilter('instanceId', event.target.value)} /></FieldGroup>
        </FilterGrid>
      </MvpCard>
      {loading ? <LoadingState label="Loading audit events�" /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <MvpCard title="Audit events">
          {data.rows.length ? (
            <>
              <DataTable columns={['Timestamp', 'Actor', 'Action', 'Target', 'Instance', 'Reason']}>
                {data.rows.map((event) => (
                  <tr key={event.id}>
                    <td>{formatDateTime(event.createdAt)}</td>
                    <td>{event.actorUser?.email || event.actorUserId}</td>
                    <td>{event.action}</td>
                    <td>{event.targetType} {event.targetId || ''}</td>
                    <td>{event.instanceId || '�'}</td>
                    <td>{event.reason || '�'}</td>
                  </tr>
                ))}
              </DataTable>
              <PaginationControls page={data.pagination.page} totalPages={data.pagination.pages} onPageChange={(nextPage) => updateFilter('page', String(nextPage))} />
            </>
          ) : <EmptyState title="No audit entries" message="No audit records matched the current filters." />}
        </MvpCard>
      ) : null}
    </MvpTalentLayout>
  );
};

export default AuditPage;
